import { ExtendableError } from '@precon-tools-suite-by-shared/js-utils';
import type { DownloadableFileFragment } from './get-downloadable-file.generated';
import type { AxiosError } from 'axios';

export interface FileDownloadErrorOptions extends ErrorOptions {
    /**
     * Whether this error is safe to display to the user in the GUI.
     *
     * @default false
     */
    isUserSafe?: boolean;
    /**
     * Whether the user can report this error.
     */
    reportable?: boolean;
    /**
     * Title of the error when displayed in a GUI. Defaults to the name of the error.
     */
    guiTitle?: string | null;
    /**
     * The message that should be shown instead of the the error-message when it is displayed in a GUI.
     */
    guiMessage?: string | null;
    /**
     * A string containing some direction on what the user should do when this error is shown.
     *
     * @default null
     */
    guiAction?: string | null;
}

export class FileDownloadError extends ExtendableError {
    public readonly isUserSafe: boolean;
    public readonly reportable: boolean;
    public readonly guiTitle: string | null;
    public readonly guiMessage: string | null;
    public readonly guiAction: string | null;
    public readonly meta: any;

    constructor(
        message?: string,
        options?: FileDownloadErrorOptions,
        meta?: any
    ) {
        super(message ?? 'FileDownloadError', options);
        this.isUserSafe = options?.isUserSafe ?? false;
        this.reportable = options?.reportable ?? false;
        this.guiTitle = options?.guiTitle ?? null;
        this.guiMessage = options?.guiMessage ?? null;
        this.guiAction = options?.guiMessage ?? null;
        this.meta = meta;
    }
}

export class FileDownloadGraphQLError extends FileDownloadError {
    static fromGraphQLErrors(
        errors: readonly unknown[]
    ): FileDownloadGraphQLError {
        return new FileDownloadGraphQLError(
            'file-graphql-errors',
            {
                guiTitle: `Fout bij ophalen bestandsgegevens`,
                guiMessage: `Probeer het later nogmaals`,
                isUserSafe: true,
                reportable: true,
            },
            { errors }
        );
    }

    static fromCaughtError(error: unknown): FileDownloadGraphQLError {
        return new FileDownloadGraphQLError('file-graphql-caught-error', {
            guiTitle: `Fout bij ophalen bestandsgegevens`,
            guiMessage: `Probeer het later nogmaals`,
            reportable: true,
            isUserSafe: true,
            cause: error,
        });
    }
}

export class FileNotFoundError extends FileDownloadError {
    static fromInputId(id: string): FileNotFoundError {
        return new FileNotFoundError(
            `file-node-not-found`,
            {
                guiTitle: `Bestand niet gevonden`,
                guiMessage: `Bestand met id ${id} niet gevonden. Dit kan gebeuren als het bestand niet meer bestaat òf als u geen toegang heeft tot het bestand.`,
                guiAction: `Controleer of het bestand nog bestaat of log in met een ander account/team.`,
                isUserSafe: true,
            },
            {
                id,
            }
        );
    }
}

export class NodeNotFoundError extends FileNotFoundError {
    static fromNodeId(id: string): NodeNotFoundError {
        return new NodeNotFoundError(
            `node-not-found`,
            {
                guiTitle: `Geen bestanden niet gevonden`,
                guiMessage: `Geen bestanden geaccocieert met ${id} gevonden. Dit kan gebeuren als een entiteit niet meer bestaat òf als u geen toegang heeft tot deze data.`,
                guiAction: `Controleer of de entiteit met bestanden nog bestaat of log-in met een ander account/team.`,
                isUserSafe: true,
            },
            {
                id,
            }
        );
    }
}

export class NoFilesError extends FileNotFoundError {
    static fromHasFilesNode(id: string): FileNotFoundError {
        return new FileNotFoundError(`node-has-no-files`, {
            guiTitle: 'Geen betanden',
            guiMessage: `Er zijn geen bestanden gevonden voor node ${id}.`,
            isUserSafe: true,
        });
    }
}

export class DownloadLinkMissingError extends FileDownloadError {
    static fromId(id: string): DownloadLinkMissingError {
        return new DownloadLinkMissingError(
            'download-link-missing',
            {
                guiTitle: `Geen downloadlink voor bestand`,
                guiMessage: `De server gaf geen downloadlink voor bestand ${id}. Dit kan gebeuren als u geen machtiging heeft om het bestand te downloaden.`,
                guiAction: `Log in met een ander account/team en probeer het nogmaals.`,
                isUserSafe: true,
            },
            { file: { id } }
        );
    }

    static fromFragment(
        file: DownloadableFileFragment
    ): DownloadLinkMissingError {
        return new DownloadLinkMissingError(
            'download-link-missing',
            {
                guiTitle: `Geen downloadlink voor bestand`,
                guiMessage: `De server gaf geen downloadlink voor bestand ${
                    file.filename ?? file.id
                }. Dit kan gebeuren als u geen machtiging heeft om het bestand te downloaden.`,
                guiAction: `Log in met een ander account/team en probeer het nogmaals.`,
                isUserSafe: true,
            },
            { file }
        );
    }
}

export class FileDownloadFailedError extends FileDownloadError {
    static fromAxiosError(error: AxiosError): FileDownloadFailedError {
        let guiMessage = `Het downloaden van het bestand is mislukt: ${error.message}`;
        if (error.response) {
            const status = error.response.status;
            const statusText = error.response.statusText;
            guiMessage = `Het downloaden van het bestand is mislukt: ${status} ${statusText}`;
        }

        return new FileDownloadFailedError(error.message, {
            guiTitle: `Download mislukt`,
            guiMessage,
            guiAction: 'Probeer het later nogmaals',
            isUserSafe: true,
            cause: error,
        });
    }
}
